import React, { Suspense, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import config from '@/config';
import useCache from '@/hooks/useCache';
import useGoBack from '@/hooks/useGoBack';
import useStateHandler from '@/hooks/useStateHandler';
import useTranslations from '@/i18n/useTranslations';
import BackArrowIcon from '@/shared-components/icons/components/public/BackArrowIcon';
import Loader from '@/shared-components/loader/Loader';

import './index.css';

const SuarteRoadContent = React.lazy(() => import('./suarte-road-content'));

const normalizeUserType = (session = {}) => {
  const candidates = [
    session?.user_type,
    session?.__t,
    session?.user_profile?.user_type,
    session?.user_profile?.__t,
  ];
  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim().length > 0) {
      return candidate.trim().toLowerCase();
    }
  }
  return null;
};

export default function SuarteRoad() {
  const t = useTranslations('common.profile.suarte_road');
  const { state, stateHandler } = useStateHandler();
  const navigate = useNavigate();
  const normalizedUserType = normalizeUserType(state.user_session);
  const isGalleryProfile = normalizedUserType === 'gallery';
  const { fetchData } = useCache(
    "user_suarte__road",
    `${config.apis.api.url}/suarteroad`,
    {
      injectToken: true,
      type: "@cache/dynamic"
    }
  );
  const goBackHandler = useGoBack("/profile");

  useEffect(() => {
    if (Boolean(fetchData) && fetchData.completed && state.user_session.user_flags.suarteroad_completed === false) {
      stateHandler.set("user_session.user_flags.suarteroad_completed", true);
    }
  }, [fetchData]);

  const isSuarteRoadCompleted = Boolean(
    state.user_session.user_flags.suarteroad_completed || fetchData?.completed
  );

  const handleRewardActivation = () => {
    if (!isSuarteRoadCompleted) return;
    navigate('/reward');
  };

  const showCompleteRoadPopup = () => {
    stateHandler.set("temporalPopup", {
      text: t('popup.complete_road_for_reward'),
      type: "navbar-present"
    });
  };

  return (
    <>
      <div className="suarte-road__page">
        <div className="suarte-road__wrapper">
          <div className="suarte-road__header">
            <span className="suarte-road__header-text font-size-large">
              {t('title')}
            </span>

            <BackArrowIcon className="suarte-road__back-button" onClick={goBackHandler} />
          </div>

          <div className="suarte-road__reward-cta">
            <div className="suarte-road__reward-button-wrapper">
              <button
                type="button"
                className="suarte-road__reward-button"
                onClick={handleRewardActivation}
                disabled={!isSuarteRoadCompleted}
              >
                {t('reward_button')}
              </button>
              {!isSuarteRoadCompleted && (
                <span
                  className="suarte-road__reward-disabled-overlay"
                  role="presentation"
                  onClick={showCompleteRoadPopup}
                />
              )}
            </div>
          </div>

          {isGalleryProfile ? (
            <div className="suarte-road__gallery-placeholder">
              <p className="suarte-road__gallery-placeholder-text">
                {t('gallery_unavailable')}
              </p>
            </div>
          ) : (
            <Suspense fallback={<Loader className="suarte-road__spinner" thin/>}>
              <SuarteRoadContent fetchData={fetchData} userType={normalizedUserType} />
            </Suspense>
          )}
        </div>
      </div>
    </>
  );
}
